import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { WorkOrderService } from '../../../core/data-services/work-order.service';
import { CustomerService } from '../../../core/data-services/customer.service';
import { FieldService } from '../../../core/data-services/field.service';
import { TeamService } from '../../../core/data-services/team.service';
import { OrganizationContextService } from '../../../core/services/organization-context.service';
import { ToastService } from '../../../core/services/toast.service';
import { CreateWorkOrderDto } from '../../../core/request-models/create-work-order-dto.model';
import { MembershipRoles } from '../../../core/data-models/membership-roles.model';
import { RoleHelper } from '../../../core/utils/membership-role-utils';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

/**
 * Work Order create form.
 *
 * The detail screen handles edits inline (PUT /work-orders/:id), so
 * this form is create-only. Required fields per the spec are just
 * `title`; everything else is optional. The picker for **Field**
 * narrows when a customer is picked.
 *
 * On success, navigates to `/work-orders/:id` so the user lands on
 * the freshly-created WO.
 */
@Component({
  selector: 'app-work-order-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, MaxLengthDirective, CharCounterDirective],
  templateUrl: './work-order-form.component.html',
  styleUrls: ['./work-order-form.component.scss'],
})
export class WorkOrderFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private workOrderService = inject(WorkOrderService);
  private customerService = inject(CustomerService);
  private fieldService = inject(FieldService);
  private teamService = inject(TeamService);
  private organizationContext = inject(OrganizationContextService);
  private toast = inject(ToastService);

  form!: FormGroup;
  isSaving = signal(false);

  /** Selected customer id, used to narrow the field picker. */
  private selectedCustomerId = signal<string | null>(null);

  readonly customers = computed(() =>
    this.customerService.customers().map((c) => ({ id: c.id, name: c.name })),
  );

  readonly fields = computed(() => {
    const customerId = this.selectedCustomerId();
    const all = this.fieldService.fields();
    const scoped = customerId
      ? all.filter((f) => f.customerId === customerId)
      : all;
    return scoped.map((f) => ({
      id: f.id,
      name: f.name,
      customerId: f.customerId ?? null,
    }));
  });

  /** Pilots + PICs + Owners — anyone who can fly a WO. */
  readonly assignablePilots = computed(() => {
    return this.teamService
      .members()
      .filter((m) => !m.deletedAt)
      .filter((m) => {
        const role = m.combinedRole ?? m.role ?? 0;
        return (
          RoleHelper.hasRole(role, MembershipRoles.Pilot) ||
          RoleHelper.hasRole(role, MembershipRoles.PilotInCharge) ||
          RoleHelper.hasRole(role, MembershipRoles.Owner)
        );
      })
      .map((m) => ({
        id: m.profileId,
        name: this.memberDisplayName(m.firstName, m.lastName, m.email),
      }));
  });

  ngOnInit(): void {
    this.form = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(2)]],
      customerId: [''],
      fieldId: [''],
      assignedPilotProfileId: [''],
      scheduledDate: [''],
      scheduledTime: [''],
      description: [''],
    });

    // React to customer changes — narrow the field picker and clear
    // any selected field that no longer belongs to the chosen customer.
    this.form.get('customerId')?.valueChanges.subscribe((customerId: string) => {
      this.selectedCustomerId.set(customerId || null);
      const fieldCtrl = this.form.get('fieldId');
      const selectedFieldId: string = fieldCtrl?.value ?? '';
      if (!selectedFieldId) return;
      const stillValid = this.fields().some((f) => f.id === selectedFieldId);
      if (!stillValid) fieldCtrl?.setValue('');
    });

    // Prime team list if cold-landed here.
    const orgId = this.organizationContext.getCurrentOrganizationId();
    if (orgId) {
      void this.teamService.loadMembers(orgId);
    }
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving.set(true);
    try {
      const v = this.form.value;
      const dto: CreateWorkOrderDto = {
        title: v.title.trim(),
        description: v.description?.trim() || undefined,
        customerId: v.customerId || null,
        fieldId: v.fieldId || null,
        assignedPilotProfileId: v.assignedPilotProfileId || null,
        scheduledDate: v.scheduledDate || null,
        scheduledTime: v.scheduledTime?.trim() || null,
      };
      const created = await this.workOrderService.createWorkOrder(dto);
      if (created) {
        this.toast.success('Work order created', { title: 'Work Order' });
        this.router.navigate(['/work-orders', created.id]);
      } else {
        this.toast.error(
          this.workOrderService.error() ?? 'Failed to create work order',
        );
      }
    } finally {
      this.isSaving.set(false);
    }
  }

  cancel(): void {
    this.router.navigate(['/work-orders']);
  }

  // Helpers ─────────────────────────────────────────────────────────

  hasError(field: string, errorName: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.touched && ctrl.errors && ctrl.errors[errorName]);
  }

  private memberDisplayName(
    firstName?: string,
    lastName?: string,
    email?: string,
  ): string {
    const name = [firstName, lastName].filter(Boolean).join(' ').trim();
    return name || email || 'Unknown';
  }
}
